import {
  AirlockFormType,
  type ConfigurationPublicViewV2,
  type ConfigurationPublicViewV3,
  type GetWorkspaceConfigurationResponse
} from './types'

export const hasWorkspaceConfiguration = (
  configuration: GetWorkspaceConfigurationResponse | undefined
): configuration is ConfigurationPublicViewV2 | ConfigurationPublicViewV3 => {
  return configuration !== undefined && 'enabled' in configuration
}

export const isNamedPolicyWorkspaceConfiguration = (
  configuration: GetWorkspaceConfigurationResponse | undefined
): configuration is ConfigurationPublicViewV3 => {
  return hasWorkspaceConfiguration(configuration) && '_id' in configuration
}

export const isAttestationEnabledForActivePolicy = (
  configuration: GetWorkspaceConfigurationResponse | undefined
): boolean => {
  if (!isNamedPolicyWorkspaceConfiguration(configuration)) {
    return false
  }

  return configuration.forms?.some(form => form.type === AirlockFormType.Attestation) ?? false
}

export const isAdditionalInformationFormConfiguredForDar = (
  configuration: GetWorkspaceConfigurationResponse | undefined
): boolean => {
  if (!isNamedPolicyWorkspaceConfiguration(configuration)) {
    return false
  }

  // Set by airlock-be when Airlock access is enabled and either the policy form or a
  // Data Access attestation assignment can be resolved.
  return configuration.airlockAccess?.additionalInformationAvailable === true
}

export const isAutoSharingOnApprovalEnabledForWorkspaceConfiguration = (
  configuration: GetWorkspaceConfigurationResponse | undefined
): boolean => {
  if (!isNamedPolicyWorkspaceConfiguration(configuration)) {
    return false
  }

  return (
    configuration.airlockAccess?.enabled === true && configuration.airlockAccess.autoSharingOnApprovalEnabled === true
  )
}

// Connect Airlock reviewers are workspace admins, so file preview and download follow that permission.
export const isExportRequestFileViewDisabled = (
  configuration: GetWorkspaceConfigurationResponse | undefined
): boolean => {
  if (!hasWorkspaceConfiguration(configuration)) {
    return false
  }

  return configuration.permissions.admins?.enableViewFile === false
}

export const getWorkspaceConfigurationDownloadLinkExpiresInSeconds = (
  configuration: GetWorkspaceConfigurationResponse | undefined
): number | undefined => {
  if (!hasWorkspaceConfiguration(configuration)) {
    return undefined
  }

  return configuration.downloadLinkExpiresInSeconds
}
